"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var classNames = require("classnames");
var Messages = require("../codegen/Messages");
var Popover_1 = require("@oui/core/Popover");
var Aria_1 = require("../resources/Aria");
var warning_1 = require("../utils/warning");
require("../utils/polyfill");
var index_1 = require("../index");
// supported visual variants
var ActionMenuItemButtonStyle;
(function (ActionMenuItemButtonStyle) {
    ActionMenuItemButtonStyle[ActionMenuItemButtonStyle["Default"] = 0] = "Default";
    ActionMenuItemButtonStyle[ActionMenuItemButtonStyle["Primary"] = 1] = "Primary";
    ActionMenuItemButtonStyle[ActionMenuItemButtonStyle["Success"] = 2] = "Success";
    ActionMenuItemButtonStyle[ActionMenuItemButtonStyle["Danger"] = 3] = "Danger";
})(ActionMenuItemButtonStyle = exports.ActionMenuItemButtonStyle || (exports.ActionMenuItemButtonStyle = {}));
var initialMenuContext = {
    closeMenu: function () { },
    closeOnSelect: true,
    focusedItem: undefined,
    selectedItem: undefined,
    onkeyUp: function () { },
    onSelect: function () { },
    selectEnabled: false
};
var MenuContext = React.createContext(initialMenuContext);
exports.ActionMenuButtonItem = function (_a) {
    var _b = _a.actionStyle, actionStyle = _b === void 0 ? ActionMenuItemButtonStyle.Default : _b, disabled = _a.disabled, _c = _a.focusable, focusable = _c === void 0 ? true : _c, label = _a.label, name = _a.name, onClick = _a.onClick, tabIndex = _a.tabIndex, testId = _a.testId, rest = __rest(_a, ["actionStyle", "disabled", "focusable", "label", "name", "onClick", "tabIndex", "testId"]);
    var _d = React.useContext(MenuContext), closeMenu = _d.closeMenu, closeOnSelect = _d.closeOnSelect, focusedItem = _d.focusedItem, onkeyUp = _d.onkeyUp, onSelect = _d.onSelect, selectEnabled = _d.selectEnabled, selectedItem = _d.selectedItem;
    // deprecate in v5, warn in v4
    // -----------------------------
    // oui-react < 4  encouraged consumers to put tabIndex 0 on this li, but that causes a11y keyboard support issues
    // as this creates an extra tab stop on this non-actionable list item.
    warning_1.default(tabIndex !== undefined, "deprecation warning, passing tabIndex to action menu button items is deprecated and will be removed in next major version.");
    var _e = __read(React.useState(null), 2), localRef = _e[0], setLocalRef = _e[1];
    React.useEffect(function () {
        if (!localRef || focusedItem !== name)
            return;
        localRef.focus();
    }, [focusedItem, localRef]);
    var handleClick = React.useCallback(function (event) {
        if (event && event.currentTarget) {
            closeOnSelect && closeMenu();
            var selectedItemKey = event.currentTarget.parentElement.getAttribute("data-name");
            onSelect(selectedItemKey);
            onClick && onClick(event);
        }
    }, [onClick, onSelect]);
    var onKeyDown = function (event) {
        switch (event.key) {
            case "ArrowUp":
            case "ArrowLeft":
            case "ArrowDown":
            case "ArrowRight":
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    };
    return (React.createElement("li", __assign({}, rest, { onClick: undefined, className: classNames({
            "oui-active": selectedItem === name,
            "oui-button-action__danger": actionStyle === ActionMenuItemButtonStyle.Danger,
            "oui-button-action__success": actionStyle === ActionMenuItemButtonStyle.Success
        }), "data-name": name, key: name }),
        React.createElement(index_1.Button, { ref: function (node) {
                setLocalRef(node);
            }, "aria-selected": selectEnabled ? selectedItem === name : undefined, buttonStyle: index_1.ButtonStyle.Action, disabled: disabled, focusable: focusable, onClick: handleClick, onKeyDown: onKeyDown, onKeyUp: onkeyUp, role: Aria_1.Roles.Menuitem, testId: testId }, label)));
};
/**
 * @oui-component ActionMenuButton
 */
var ActionMenuButton = function (_a) {
    var children = _a.children, _b = _a.closeOnSelect, closeOnSelect = _b === void 0 ? true : _b, disabled = _a.disabled, _c = _a.focusable, focusable = _c === void 0 ? true : _c, label = _a.label, id = _a.id, _d = _a.isRetainSelection, isRetainSelection = _d === void 0 ? false : _d, _e = _a.placement, placement = _e === void 0 ? "bottom-left" : _e, selectedItem = _a.selectedItem, testId = _a.testId, rest = __rest(_a, ["children", "closeOnSelect", "disabled", "focusable", "label", "id", "isRetainSelection", "placement", "selectedItem", "testId"]);
    var _f = __read(React.useState(selectedItem), 2), selected = _f[0], setSelected = _f[1];
    var _g = __read(React.useState(0), 2), focusIndex = _g[0], setFocusIndex = _g[1];
    var _h = Popover_1.usePopoverState(), bind = _h.bind, bindDisclosure = _h.bindDisclosure;
    // @todo - remove managing visible state, once usePopoverState() can support onClickOutside behavior to close Popover (OUI-770)
    var _j = __read(React.useState(false), 2), visible = _j[0], setVisible = _j[1];
    // close menu on clicks external to Button (disclosure) and Menu
    // @todo handleClickOutside, openMmnu and closeMenu can all be replaced once OUI-770 is addressed
    var handleClickOutside = function (event) {
        if (!bind.anchor.contains(event.target) &&
            event.target.closest("#" + bind.id) === null) {
            closeMenu();
        }
    };
    var openMenu = function () { return setVisible(true); };
    var closeMenu = function () {
        setVisible(false);
        bind.anchor &&
            typeof bind.anchor.focus === "function" &&
            bind.anchor.focus({ preventScroll: true });
    };
    React.useEffect(function () {
        if (visible) {
            document.addEventListener("mouseup", handleClickOutside);
        }
        return function () {
            document.removeEventListener("mouseup", handleClickOutside);
        };
    }, [handleClickOutside, visible]);
    var handleClick = function (event) {
        visible ? closeMenu() : openMenu();
        bindDisclosure.onClick(event);
    };
    var selectEnabled = selectedItem || isRetainSelection ? true : false;
    var buttonProps = {
        disabled: disabled,
        focusable: focusable,
        id: id,
        testId: testId,
        "aria-controls": bindDisclosure["aria-controls"],
        "aria-expanded": bindDisclosure["aria-expanded"],
        "aria-haspopup": true,
        buttonIcon: index_1.Icon.TriangleDown,
        buttonStyle: index_1.ButtonStyle.Default,
        iconPlacement: index_1.ButtonIconPlacement.After,
        onClick: handleClick
    };
    var menuItems = React.Children.map(children, function (child) { return child && child.props.name; });
    var onSelect = function (buttonName) {
        if (selectEnabled === true) {
            setSelected(buttonName);
            setFocusIndex(menuItems.indexOf(buttonName));
        }
    };
    var onkeyUp = function (event) {
        var next = function () {
            var idx = focusIndex + 1;
            if (idx < menuItems.length) {
                setFocusIndex(idx);
            }
        };
        var previous = function () {
            var idx = focusIndex - 1;
            if (idx >= 0) {
                setFocusIndex(idx);
            }
        };
        switch (event.key) {
            // @todo - OUI-608 or react V5 will  arrow key support to the table cell action menu, at which point Tab should exit the "set"
            case "Tab":
                event.shiftKey ? previous() : next();
                event.preventDefault();
                break;
            case "ArrowUp":
            case "ArrowLeft":
                previous();
                event.preventDefault();
                break;
            case "ArrowDown":
            case "ArrowRight":
                next();
                event.preventDefault();
                break;
            case "Home":
                setFocusIndex(0);
                event.preventDefault();
                break;
            case "End":
                setFocusIndex(menuItems.length - 1);
                event.preventDefault();
                break;
            case "Escape":
                closeMenu();
                event.stopPropagation();
                break;
        }
    };
    var contextProps = {
        closeMenu: closeMenu,
        closeOnSelect: closeOnSelect,
        onkeyUp: onkeyUp,
        onSelect: onSelect,
        selectEnabled: selectEnabled,
        selectedItem: selected,
        focusedItem: menuItems[focusIndex]
    };
    var popoverProps = __assign({}, bind, { placement: placement,
        visible: visible });
    return (React.createElement("div", __assign({}, rest, { className: "oui-inline" }),
        React.createElement(index_1.Button, __assign({}, buttonProps), label || Messages.Common.actions()),
        React.createElement(Popover_1.default, __assign({}, popoverProps, { offset: { top: "none", left: "none" } }),
            React.createElement(MenuContext.Provider, { value: contextProps },
                React.createElement("ul", { className: "oui-nav", role: Aria_1.Roles.Menu, 
                    // until such time that oui-react modals are in the @oui/core layer manager we need to make sure that the pop-over is higher z-index than the modal (z-index: 1010)
                    style: { position: "relative", zIndex: 2010 } }, children)))));
};
exports.default = ActionMenuButton;
//# sourceMappingURL=ActionMenuButton.js.map